import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';
import { addressBody } from '../modules/sales/Prices/priceLookupShared';
import { PostcodeCheckPill } from './AddressFields';
import './StyledInput.css';

// A native <select> can only render flat single-line text, so an
// addressLabel() string ("Ship To — 123 Example St, Suburb STATE 1234,
// Australia") read as one run-on line with no visual hierarchy between
// the Address Type (the thing that actually distinguishes a Customer's
// saved addresses from each other) and the address itself. This gives
// Type its own quiet label above a normal-weight address line, same
// click-outside-to-close mechanics as ColumnPicker.js. `addresses` is
// already loaded client-side by the caller (fetchDeliveryAddresses below)
// — no search-as-you-type needed, unlike the Customer picker this
// otherwise mirrors visually.
// The Quote/Sales Order delivery address list for a Customer — its own
// addresses plus its linked Individuals' (each tagged with `recipient`),
// see deliveryAddressService.js. keepAddressId is the address the document
// already holds, kept in the list even if it no longer qualifies.
export function fetchDeliveryAddresses(customerId, keepAddressId) {
  return axios
    .get(`/api/customers/${customerId}/delivery-addresses`, { params: keepAddressId ? { keep: keepAddressId } : {} })
    .then((res) => res.data.data || []);
}

const typeLabelStyle = { fontSize: 11, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4 };

// "Jane Smith · Employee" — who an address belongs to when it isn't the
// Customer's own.
function recipientLabel(r) {
  return r.role ? `${r.name} · ${r.role}` : r.name;
}

// An Australian Address Australia Post didn't match, or that couldn't be
// checked (#158), flagged where it's picked.
function PostcodeFlag({ address }) {
  if (!address.postcode_check || address.postcode_check === 'matched') return null;
  return <div style={{ marginTop: 2 }}><PostcodeCheckPill status={address.postcode_check} /></div>;
}

function AddressPicker({ addresses, value, onChange, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selected = addresses.find((a) => String(a.id) === String(value));
  // A heading row goes above the first address of each linked Individual
  // (addresses arrive grouped: the Customer's own first, then each
  // Individual's together).
  const headingFor = (a, i) => {
    if (!a.recipient) return null;
    const prev = addresses[i - 1];
    return prev?.recipient && String(prev.recipient.id) === String(a.recipient.id) ? null : recipientLabel(a.recipient);
  };
  const emptyLabel = placeholder || (addresses.length ? '(none selected)' : 'No delivery addresses for this Customer');

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="styled-input"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
          width: '100%', minHeight: 30, height: 'auto', padding: '5px 8px', fontSize: 14, textAlign: 'left',
          background: disabled ? '#f3f4f6' : '#fff', color: selected ? '#111' : '#888',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {selected ? (
          <span style={{ minWidth: 0 }}>
            {selected.address_type?.name && (
              <div style={typeLabelStyle}>{selected.address_type.name}</div>
            )}
            {selected.recipient && (
              <div style={{ fontWeight: 600, color: '#333' }}>{recipientLabel(selected.recipient)}</div>
            )}
            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', color: '#333' }}>{addressBody(selected)}</div>
            <PostcodeFlag address={selected} />
          </span>
        ) : (
          <span>{emptyLabel}</span>
        )}
        <ChevronDown size={14} style={{ flexShrink: 0, color: '#888', marginTop: 2 }} />
      </button>
      {open && (
        <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, margin: '4px 0 0', padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto' }}>
          <li
            onClick={() => { onChange(''); setOpen(false); }}
            style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4, color: '#888' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            (none selected)
          </li>
          {addresses.map((a, i) => {
            const heading = headingFor(a, i);
            return (
              <React.Fragment key={a.id}>
                {heading && (
                  <li style={{ padding: '8px 8px 2px', marginTop: 4, borderTop: '1px solid #e5e9f0', fontSize: 12, fontWeight: 600, color: '#555' }}>
                    {heading}
                  </li>
                )}
                <li
                  onClick={() => { onChange(String(a.id)); setOpen(false); }}
                  style={{ padding: '6px 8px', paddingLeft: a.recipient ? 16 : 8, cursor: 'pointer', borderRadius: 4 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {a.address_type?.name && (
                    <div style={typeLabelStyle}>{a.address_type.name}</div>
                  )}
                  <div style={{ fontSize: 13, color: '#333' }}>{addressBody(a)}</div>
                  <PostcodeFlag address={a} />
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AddressPicker;
